import { createTheme } from '@mui/material/styles';

// ── Central XEO theme ───────────────────────────────────────────────────────
// Single source of truth for the dashboard look. Introduced to unify the
// 40+ pages that previously hardcoded their own colors / spacing / button
// styles inline (which is why Add Domain and Add Template looked nothing
// alike). MUI components inherit these defaults, so this aligns palette,
// typography, rounding, buttons, inputs and cards app-wide.
//
// IMPORTANT — this is purely visual and non-breaking by design:
//   • Inline `sx` on a component always overrides the theme, so pages that
//     already style themselves deliberately (e.g. the XSite builder) are
//     unaffected. The theme only "catches" the plain, un-styled components
//     that were the actual source of inconsistency.
//   • No workflow, prop, or sizing defaults are changed — colors, radius,
//     weight and font only. Nothing that alters layout flow or behavior.
// ── Colour modes ─────────────────────────────────────────────────────────────
// `makeTheme('light' | 'dark')` builds the theme for a mode; ColorModeProvider
// picks which one is live (explicit choice, or the OS via prefers-color-scheme).
//
// The dark palette is NOT an inversion. Grounds are a cool near-navy rather than
// black so the brand blue still reads as blue on them; the accent is lifted
// (#1976d2 is too dark to sit on a dark ground); semantic colours move toward
// their lighter ends for contrast; and text tops out at #e8edf8, because pure
// white on dark vibrates.
const PALETTES = {
  light: {
    primary:   { main: '#1976d2', dark: '#0d47a1', light: '#e3f2fd', contrastText: '#ffffff' },
    secondary: { main: '#6366f1', dark: '#4f46e5', light: '#eef0fa', contrastText: '#ffffff' },
    success:   { main: '#16a34a', light: '#dcfce7', dark: '#15803d' },
    warning:   { main: '#f59e0b', light: '#fef3c7', dark: '#b45309' },
    error:     { main: '#dc2626', light: '#fee2e2', dark: '#991b1b' },
    info:      { main: '#0ea5e9', light: '#e0f2fe', dark: '#0369a1' },
    text:      { primary: '#0f172a', secondary: '#64748b', disabled: '#94a3b8' },
    divider:   '#e6eaf2',
    background: { default: '#f8fafc', paper: '#ffffff' },
  },
  dark: {
    primary:   { main: '#63a4f0', dark: '#3b82f6', light: '#1e3a5f', contrastText: '#08111f' },
    secondary: { main: '#a5b4fc', dark: '#818cf8', light: '#26305a', contrastText: '#08111f' },
    success:   { main: '#4ade80', light: '#12301d', dark: '#22c55e' },
    warning:   { main: '#fbbf24', light: '#332512', dark: '#f59e0b' },
    error:     { main: '#f87171', light: '#3b1618', dark: '#ef4444' },
    info:      { main: '#5cc4f5', light: '#0d2a3d', dark: '#0ea5e9' },
    text:      { primary: '#e8edf8', secondary: '#a3b0c9', disabled: '#6f7d99' },
    divider:   '#243352',
    background: { default: '#0b1120', paper: '#111a2e' },
  },
};

export const makeTheme = (mode = 'light') => {
  const dark = mode === 'dark';
  const P = PALETTES[dark ? 'dark' : 'light'];
  // Named surfaces, so the component overrides below stop hardcoding hexes and
  // one edit moves both modes.
  const S = dark
    ? { line: '#243352', line2: '#33456b', head: '#152036', rule: '#1b2842', ink2: '#a3b0c9', ink3: '#6f7d99', ink4: '#5f6f93', surface: '#111a2e', sunken: '#0d1526', tint: 'rgba(99,164,240,0.10)', tintHover: 'rgba(99,164,240,0.18)', ring: 'rgba(99,164,240,0.22)', shadowInk: '0,0,0', wash: 'rgba(255,255,255,0.07)', washSheen: 'rgba(255,255,255,0.06)', tipBg: '#26334f', scrim: 'rgba(3,7,18,0.62)' }
    : { line: '#e6eaf2', line2: '#cbd5e1', head: '#f8faff', rule: '#eef1f7', ink2: '#475569', ink3: '#94a3b8', ink4: '#7c8aa5', surface: '#ffffff', sunken: '#f8fafc', tint: 'rgba(25,118,210,0.06)', tintHover: 'rgba(25,118,210,0.14)', ring: 'rgba(25,118,210,0.12)', shadowInk: '15,23,42', wash: 'rgba(15,23,42,0.05)', washSheen: 'rgba(15,23,42,0.045)', tipBg: '#0f1f3d', scrim: 'rgba(15,23,42,0.38)' };
  return createTheme({
  palette: { mode: dark ? 'dark' : 'light', ...P },

  shape: { borderRadius: 10 },

  // One scale, set once. Headings tighten their tracking as they grow (large
  // type needs less air between letters, small type needs more), and body copy
  // runs at 1.6 so paragraphs breathe without extra margins. Sizes are in rem so
  // a browser's font-size setting still scales the whole app.
  // Headings are MOBILE-FIRST: the base size is the phone size, and the desktop
  // size arrives at >=600px. A fixed 2.25rem h1 on a 390px screen wraps a
  // two-word page title onto two lines and crowds everything beneath it.
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    h1: { fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.03em', '@media (min-width:600px)': { fontSize: '2.25rem' } },
    h2: { fontSize: '1.42rem', fontWeight: 800, lineHeight: 1.18, letterSpacing: '-0.025em', '@media (min-width:600px)': { fontSize: '1.85rem' } },
    h3: { fontSize: '1.3rem', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.022em', '@media (min-width:600px)': { fontSize: '1.6rem' } },
    h4: { fontSize: '1.18rem', fontWeight: 800, lineHeight: 1.25, letterSpacing: '-0.02em', '@media (min-width:600px)': { fontSize: '1.4rem' } },
    h5: { fontSize: '1.08rem', fontWeight: 800, lineHeight: 1.3, letterSpacing: '-0.015em', '@media (min-width:600px)': { fontSize: '1.2rem' } },
    h6: { fontSize: '0.96rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: '-0.01em', '@media (min-width:600px)': { fontSize: '1.02rem' } },
    subtitle1: { fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.92rem', lineHeight: 1.6 },
    body2: { fontSize: '0.85rem', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    // Uppercase micro-labels need tracking or they read as a smudge.
    overline: { fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.09em', lineHeight: 1.4 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },

  // MUI's stock ramp is a grey drop shadow that reads as heavy and slightly
  // dirty on a light ground. This one is a hairline plus a wide, very soft
  // ambient shadow — the surface lifts without announcing it.
  shadows: [
    'none',
    '0 1px 2px rgba(15,23,42,0.04)',
    '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px -6px rgba(15,23,42,0.10)',
    '0 1px 2px rgba(15,23,42,0.04), 0 8px 20px -10px rgba(15,23,42,0.13)',
    '0 1px 2px rgba(15,23,42,0.04), 0 12px 28px -14px rgba(15,23,42,0.16)',
    '0 1px 2px rgba(15,23,42,0.04), 0 14px 32px -16px rgba(15,23,42,0.18)',
    '0 1px 3px rgba(15,23,42,0.05), 0 18px 40px -20px rgba(15,23,42,0.20)',
    '0 1px 3px rgba(15,23,42,0.05), 0 20px 44px -22px rgba(15,23,42,0.21)',
    '0 1px 3px rgba(15,23,42,0.05), 0 24px 52px -26px rgba(15,23,42,0.23)',
    '0 1px 3px rgba(15,23,42,0.05), 0 26px 56px -28px rgba(15,23,42,0.24)',
    '0 2px 4px rgba(15,23,42,0.06), 0 28px 60px -30px rgba(15,23,42,0.25)',
    '0 2px 4px rgba(15,23,42,0.06), 0 30px 64px -32px rgba(15,23,42,0.26)',
    '0 2px 4px rgba(15,23,42,0.06), 0 32px 68px -34px rgba(15,23,42,0.27)',
    '0 2px 4px rgba(15,23,42,0.06), 0 34px 72px -36px rgba(15,23,42,0.28)',
    '0 2px 5px rgba(15,23,42,0.07), 0 36px 76px -38px rgba(15,23,42,0.29)',
    '0 2px 5px rgba(15,23,42,0.07), 0 38px 80px -40px rgba(15,23,42,0.30)',
    '0 2px 5px rgba(15,23,42,0.07), 0 40px 84px -42px rgba(15,23,42,0.31)',
    '0 2px 6px rgba(15,23,42,0.08), 0 42px 88px -44px rgba(15,23,42,0.32)',
    '0 2px 6px rgba(15,23,42,0.08), 0 44px 92px -46px rgba(15,23,42,0.33)',
    '0 2px 6px rgba(15,23,42,0.08), 0 46px 96px -48px rgba(15,23,42,0.34)',
    '0 3px 7px rgba(15,23,42,0.09), 0 48px 100px -50px rgba(15,23,42,0.35)',
    '0 3px 7px rgba(15,23,42,0.09), 0 50px 104px -52px rgba(15,23,42,0.36)',
    '0 3px 8px rgba(15,23,42,0.10), 0 52px 108px -54px rgba(15,23,42,0.37)',
    '0 3px 8px rgba(15,23,42,0.10), 0 54px 112px -56px rgba(15,23,42,0.38)',
    '0 4px 10px rgba(15,23,42,0.11), 0 56px 116px -58px rgba(15,23,42,0.40)',
  ],

  // One easing for everything that moves, so hovers across the app feel like
  // the same hand made them.
  transitions: {
    easing: { easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)' },
    duration: { shortest: 120, shorter: 160, short: 200, standard: 240 },
  },

  components: {
    // NOTE: no MuiCssBaseline block — <CssBaseline/> is not rendered anywhere in
    // this app, so overrides here would be dead config. The page ground and the
    // document-level scroll rules live in index.css instead.

    // The sidebar is the one component on literally every screen. Rounded rows
    // with a tinted selected state read as navigation; full-bleed grey bars read
    // as a list.
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'background-color .16s ease, color .16s ease',
          '&:hover': { backgroundColor: S.tint },
          '&.Mui-selected': {
            backgroundColor: S.tint,
            '&:hover': { backgroundColor: S.tintHover },
          },
        },
      },
    },

    // Tabs: a short rounded indicator instead of a full-width hairline, and
    // labels that don't shout.
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 700, fontSize: '0.86rem', minHeight: 44,
          transition: 'color .16s ease',
        },
      },
    },

    // Menus and popovers float, so they get the soft ambient shadow rather than
    // MUI's grey one.
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12, border: `1px solid ${S.line}`,
          backgroundColor: S.surface, backgroundImage: 'none',
          boxShadow: `0 1px 2px rgba(${S.shadowInk},0.04), 0 18px 40px -20px rgba(${S.shadowInk},0.22)`,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          // An explicit OPAQUE surface. A floating panel that inherits
          // `transparent` lets the page show through, which is what made the
          // status dropdowns look like they had no background at all.
          backgroundColor: S.surface,
          backgroundImage: 'none',
          border: `1px solid ${S.line}`,
          boxShadow: `0 1px 2px rgba(${S.shadowInk},0.04), 0 18px 40px -20px rgba(${S.shadowInk},0.22)`,
        },
      },
    },

    // Select's own dropdown, Autocomplete's listbox and every MenuItem: same
    // surface, same hover and selected tints as the sidebar, so a dropdown never
    // looks like a different product.
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.86rem',
          '&:hover': { backgroundColor: S.tint },
          '&.Mui-selected': { backgroundColor: S.tint, '&:hover': { backgroundColor: S.tintHover } },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: S.surface, backgroundImage: 'none',
          border: `1px solid ${S.line}`, borderRadius: 12,
          boxShadow: `0 1px 2px rgba(${S.shadowInk},0.04), 0 18px 40px -20px rgba(${S.shadowInk},0.22)`,
        },
        option: {
          fontSize: '0.86rem',
          '&[aria-selected="true"]': { backgroundColor: S.tint },
          '&.Mui-focused': { backgroundColor: S.tint },
        },
      },
    },
    MuiList: {
      styleOverrides: { root: { backgroundColor: 'transparent' } },
    },

    // Skeletons: square corners next to rounded cards is the tell that a
    // loading state was an afterthought.
    MuiSkeleton: {
      // 'wave' by default: MUI's 'pulse' is a two-step opacity blink that reads as
      // flashing on a light ground, where a sweep reads as work in progress.
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: {
          borderRadius: 8, backgroundColor: S.wash,
          '&::after': { background: `linear-gradient(90deg, transparent, ${S.washSheen}, transparent)` },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        // Off must read as a control (>=3:1 on the surface): an --ink-4 track,
        // and a hairline ring so the white thumb holds its edge where it
        // overhangs the track onto a white card.
        switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1 } },
        track: { borderRadius: 999, backgroundColor: S.ink4, opacity: 1 },
        thumb: { boxShadow: `0 0 0 1px ${S.line2}, 0 1px 2px rgba(${S.shadowInk},0.18)` },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, height: 5, backgroundColor: S.wash },
        bar: { borderRadius: 999 },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { transition: 'background-color .16s ease, color .16s ease' },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: S.line } },
    },

    // Table rhythm: taller rows and a hairline rule read as calmer than MUI's
    // default 1px-grey-on-everything.
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: S.rule, paddingTop: 11, paddingBottom: 11 },
        head: { fontWeight: 800, fontSize: '0.78rem', color: S.ink2, backgroundColor: S.head },
      },
    },

    // Buttons: no more shouty ALL-CAPS, consistent rounding + weight. Many
    // pages set `textTransform:'none'` by hand today — this makes it the
    // default so they don't have to (and the ones that forgot get it too).
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 700, borderRadius: 10, letterSpacing: 0,
          paddingLeft: 16, paddingRight: 16,
          transition: 'background-color .18s ease, border-color .18s ease, box-shadow .18s ease, transform .18s ease',
          // Icons sat a touch large next to 700-weight labels.
          '& .MuiButton-startIcon > *:first-of-type': { fontSize: 18 },
          '& .MuiButton-endIcon > *:first-of-type': { fontSize: 18 },
          // Button content spacing, done ONCE with gap instead of per-icon margins.
          //
          // Two bugs shared a root cause here. MUI spaces startIcon/endIcon with
          // margins, so a spinner rendered as a DIRECT child (~140 places do that)
          // got no spacing at all and sat flush against its label. And the previous
          // fix for icon-only buttons keyed on `.MuiButton-startIcon:only-child`,
          // which matches whenever the icon span is the only ELEMENT child — a text
          // label is a text NODE, so that zeroed the gap on every labelled icon
          // button in the app. "⟳Saving…" instead of "⟳ Saving…", everywhere.
          //
          // Gap fixes both and can't have that failure mode. The Button root is
          // inline-flex and a run of text becomes an anonymous flex item, so a
          // column-gap spaces text from an adjacent icon or spinner without needing
          // an element to target. Zeroing the icon margins is what makes it safe to
          // apply unconditionally: an icon-only button is a single flex item, so
          // there is no gap to leave behind, and nothing double-spaces.
          columnGap: 8,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
          // A long label can't push a button past its container.
          maxWidth: '100%',
        },
        // Deliberate heights, so a row of buttons lines up whatever size each
        // page happened to pass.
        sizeSmall: { minHeight: 32, fontSize: '0.8rem', paddingLeft: 12, paddingRight: 12 },
        sizeMedium: { minHeight: 38, fontSize: '0.86rem' },
        sizeLarge: { minHeight: 46, fontSize: '0.95rem' },
        // A filled button is a physical thing: a hairline of light along its top
        // edge, a shadow tinted with its own colour rather than grey, and a
        // deepening shadow on hover that flattens on press. That is the
        // difference between "a blue rectangle" and a control that feels
        // pressable.
        //
        // THE LIFT IS A SHADOW, NOT A MOVE. It used to be `translateY(-1px)` on
        // hover, and a transform moves the element's HIT BOX with it. Rest the
        // cursor in the bottom pixel of a button and it lifts out from under the
        // pointer -> mouseleave -> the transform is removed -> the button drops
        // back under the pointer -> mouseenter -> it lifts again. The cursor
        // flickers between pointer and arrow for as long as you hold still, on
        // every filled button in the app, because this is the theme.
        //
        // A shadow paints outside the box and changes nothing about hit testing,
        // so it reads as the same lift and cannot start that loop.
        contained: {
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(15,23,42,0.10)',
          '&:hover': {
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22), 0 10px 20px -10px rgba(25,118,210,0.62)',
          },
          '&:active': { boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.18)' },
          '&.Mui-disabled': { backgroundColor: S.line, color: S.ink3, boxShadow: 'none' },
        },
        // Outlined keeps whatever colour the page asked for; what it gains is a
        // white ground (so it reads as a surface, not a hole) and a real hover.
        outlined: {
          backgroundColor: S.surface,
          '&:hover': { backgroundColor: S.sunken },
          '&.Mui-disabled': { backgroundColor: S.sunken, borderColor: S.line, color: S.ink3 },
        },
        text: { '&:hover': { backgroundColor: S.tint } },
      },
    },

    // Inputs: one consistent look for EVERY outlined field/select platform-wide —
    // soft slate border, blue hover, 2px primary focus ring + rounded corners.
    // This is what aligns the search bars, status selects and all form fields
    // (previously the status selects fell back to MUI's default grey border).
    // ONE owner for the page gutter: the content pane (Dashboard.js) already pads
    // { xs: 2, sm: 3 }. 33 pages then let their <Container> add its own 16px on top
    // while 22 zeroed it out, so how far content sat from the screen edge depended
    // on which page you were looking at — some comfortable, some tight. Turning the
    // Container's gutters off by default makes it uniform; a page that wants more
    // still sets its own padding.
    MuiContainer: {
      defaultProps: { disableGutters: true },
    },

    // Controls never exceed their container. Note this is only half the story:
    // CSS gives min-width priority over max-width, so a control with an explicit
    // minWidth larger than the space available still overflows — those are set
    // responsively at their call sites.
    MuiFormControl: {
      styleOverrides: { root: { maxWidth: '100%' } },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: S.surface,
          transition: 'box-shadow .15s ease, border-color .15s ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: S.line, transition: 'border-color .15s ease' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: S.line2 },
          // Soft focus ring instead of a thicker border, so the field doesn't "jump" 1px on
          // focus (the old borderWidth:2 did). Consistent, modern focus across every field.
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${S.ring}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2', borderWidth: 1 },
          '&.Mui-disabled': { backgroundColor: S.sunken },
        },
      },
    },

    // Labels: consistent resting colour + a clear focused/filled state, so a field with a
    // floating label and one with a placeholder read as the same family.
    MuiFormLabel: {
      styleOverrides: {
        root: { userSelect: 'none' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: 'var(--ink-3)', '&.Mui-focused': { color: P.primary.main } },
      },
    },

    // Cards: only unify the corner rounding. We deliberately DON'T force a
    // border or shadow here — many pages use <Card> as an inline sub-element
    // (e.g. the title/description cell in the Domains table), and a global
    // border made those look boxed-in and messy. Pages that want a bordered
    // surface add it via sx (the search bars already do).
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 14 },
      },
    },

    // Rounded Paper surfaces (menus, dialogs, autocomplete dropdowns).
    MuiPaper: {
      styleOverrides: { rounded: { borderRadius: 12 } },
    },

    // maxWidth caps a chip at its container. Chips carry user-supplied text here —
    // keywords, domains, article titles — and an unbounded one widens whatever
    // holds it: a long keyword pushed a Job Details panel past its grid column and
    // spilled the next chip over the panel beside it. MUI already clips the label,
    // it just never bounded the chip.
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, maxWidth: '100%' } },
    },

    // The other half of the same bug. A flex item defaults to min-width:auto, so a
    // Grid item refuses to shrink below its widest child and overflows the row
    // instead — which is why capping the chip alone wasn't enough. Grid columns are
    // percentage-width by design, so allowing them to shrink is what was intended.
    MuiGrid: {
      styleOverrides: { item: { minWidth: 0 } },
    },

    // Standard alerts take the tone tokens (index.css), which flip with the
    // colour mode. MUI's own darkened defaults rendered near-black slabs on the
    // dark navy cards ("This job is ready to continue", "Already built.") —
    // audit 2026-11-01. The icon takes the text colour.
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
        standardInfo: { backgroundColor: 'var(--info-bg)', color: 'var(--info-fg)', '& .MuiAlert-icon': { color: 'inherit' } },
        standardWarning: { backgroundColor: 'var(--warn-bg)', color: 'var(--warn-fg)', '& .MuiAlert-icon': { color: 'inherit' } },
        standardSuccess: { backgroundColor: 'var(--good-bg)', color: 'var(--good-fg)', '& .MuiAlert-icon': { color: 'inherit' } },
        standardError: { backgroundColor: 'var(--bad-bg)', color: 'var(--bad-fg)', '& .MuiAlert-icon': { color: 'inherit' } },
      },
    },

    // Tooltips: one consistent dark style platform-wide.
    MuiTooltip: {
      // NON-INTERACTIVE BY DEFAULT.
      //
      // MUI v5 tooltips are interactive unless told otherwise, which means the
      // popper accepts pointer events. A tooltip that opens under the cursor
      // therefore takes the hover from the control that opened it: the control
      // sees mouseleave -> the tooltip closes -> the control sees mouseenter ->
      // it opens again. The cursor flickers between the control's pointer and
      // the popper's arrow, and the tooltip strobes with it.
      //
      // Interactivity exists so a tooltip can hold a link you move onto. Every
      // one of the 320 in this app is informational text — none contains
      // anything clickable — so none of them needs to accept a pointer, and 75
      // had already opted out one at a time.
      defaultProps: { disableInteractive: true },
      styleOverrides: {
        tooltip: { fontSize: '0.72rem', borderRadius: 8, backgroundColor: S.tipBg, padding: '6px 10px' },
        arrow: { color: S.tipBg },
      },
    },

    // Table pagination: on phones the "rows per page" footer used to overflow
    // and force a horizontal scrollbar under every table. One unified fix —
    // drop the verbose label, tighten spacing, and let the controls wrap and
    // centre so the footer always fits the screen (no scrollbar). Applies to
    // every table app-wide; pages with their own explicit sx still win.
    MuiTablePagination: {
      styleOverrides: {
        root: { '@media (max-width:600px)': { overflow: 'hidden' } },
        toolbar: {
          '@media (max-width:600px)': {
            paddingLeft: 8,
            minHeight: 48,
            flexWrap: 'wrap',
            justifyContent: 'center',
            rowGap: '2px',
          },
        },
        spacer: { '@media (max-width:600px)': { flex: 'none' } },
        selectLabel: { '@media (max-width:600px)': { display: 'none' } },
        displayedRows: { '@media (max-width:600px)': { margin: 0 } },
        // Rows-per-page on a phone was a 29px target (4 + 20 + 5); 6px each side
        // makes it 32px with the number still centred — for every phone list.
        select: { '@media (max-width:600px)': { paddingTop: 6, paddingBottom: 6 } },
        input: { '@media (max-width:600px)': { marginLeft: 4, marginRight: 12 } },
        actions: { '@media (max-width:600px)': { marginLeft: 4 } },
      },
    },

    // Dialogs scroll when content is tall, but the visible scrollbar (especially
    // the one that appears to sit "outside" the rounded card) looks off. Hide it
    // everywhere — the dialog paper and its content still scroll, just cleanly.
    // Applies to every dialog app-wide.
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: `0 1px 3px rgba(${S.shadowInk},0.05), 0 40px 84px -42px rgba(${S.shadowInk},0.38)`,
          // Same opaque surface as menus/popovers: no dark-mode elevation overlay
          // (it turned the paper slate grey and pushed ink-3/accent text under 4.5:1).
          backgroundColor: S.surface, backgroundImage: 'none',
        },
        // A full-screen dialog (phones) is the screen, so it has no corners. The
        // 20px paper above outranks MUI's own fullScreen 0, so every dialog that
        // didn't pin its own 0 showed rounded corners against the screen edge.
        paperFullScreen: { borderRadius: 0 },
      },
    },
    // A blurred scrim keeps the page recognisable behind a dialog instead of
    // flattening it to grey.
    //
    // Menus, Selects and Autocomplete popups also render inside a Modal, but
    // theirs asks to be INVISIBLE — a dropdown is not a modal moment, and people
    // open a status filter precisely to see the rows it will affect. `invisible`
    // is a class on the same element as `root`, so a plain root override wins on
    // specificity and every dropdown in the app was dimming and blurring the
    // page behind it. Opt the invisible variant back out explicitly.
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: S.scrim,
          backdropFilter: 'blur(2px)',
          '&.MuiBackdrop-invisible': { backgroundColor: 'transparent', backdropFilter: 'none' },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { scrollbarWidth: 'none',},
      },
    },
  },
  });
};

// Default export stays a ready-made LIGHT theme, so anything importing the theme
// directly keeps working while the app moves onto the provider.
const theme = makeTheme('light');
export default theme;
